// Prequalification funnel — sessionStorage persistence (§7).
//
// Per-role versioned keys store the whole in-progress funnel
// ({form, stepIndex, maxReached, finalized}); the Deal Explorer keeps its
// scenario under its own key. State is KEPT after finalize (the Result
// route reads it) and cleared only by the quiet "Start over" control.
// Restores are defensive: a stale/invalid/other-version blob is ignored.
//
// ── SCOPE ────────────────────────────────────────────────────────────────
//
// One slot per role, ONE tab, and — by design — more than one applicant:
// brokers are encouraged to run this funnel in person, so back-to-back
// clients in the same browser is the ordinary case, not an edge one. The
// stored blob holds a name, an email, a phone number and every answer, so
// "which application is this" has to be part of the store rather than an
// assumption about who is sitting there.
//
// A blob therefore carries the FINGERPRINT of the context it was captured
// in — the partner slug, the invite token and the /buy continuation token, all
// known synchronously from the URL at mount. `loadScopedFunnelState` compares
// it and, on a mismatch, REMOVES the blob rather than merely declining to
// restore it: bypassing leaves the previous applicant's contact details
// sitting in the tab for whatever reads next.
//
// The fingerprint lives INSIDE the single slot rather than in the key.
// A key-per-context would leave one blob per brand/invite ever visited,
// unbounded and un-removable — exactly the data we are trying to get rid of.
//
// `loadFunnelState` stays deliberately scope-BLIND. It is what the Result
// route reads, and the result URL carries no invite token — the funnel
// navigates to `{base}/{role}/result` after finalize — so enforcing the
// fingerprint there would blank the screen the applicant was just sent to.
//
// The keys are NOT versioned past v1 for this, because the scope field is
// itself the discriminator and it is strictly additive: a blob written before
// it still parses, reads back with a null scope, can therefore never match a
// real context, and is removed on the first scoped read. A key bump would add
// nothing to that and would cost a live session its just-finished application
// on the Result route across a deploy.
//
// ADDING A MEMBER to the fingerprint invalidates every blob written before the
// deploy — the array gains a slot, so no old string can match a new one and the
// first scoped read clears it. That is acceptable for the same reason a key
// bump is not needed: the only surface a mid-deploy applicant would notice is
// the Result route, and that reads through the scope-BLIND `loadFunnelState`,
// so a just-finished application is unaffected. Someone genuinely mid-funnel
// across a deploy starts the questions again. Weigh that before adding a
// fourth.

import type { PrequalFinalizeResult } from "../../api/prequalification";

export const PREQUAL_BUYER_STORAGE_KEY = "phx:prequal:buyer:v1";
export const PREQUAL_SELLER_STORAGE_KEY = "phx:prequal:seller:v1";
export const PREQUAL_EXPLORER_STORAGE_KEY = "phx:prequal:explorer:v1";

export interface StoredFunnelState<F> {
  form: F;
  stepIndex: number;
  maxReached: number;
  finalized: PrequalFinalizeResult | null;
}

/**
 * Which application a stored blob belongs to.
 *
 * Every member is readable synchronously at mount — the slug from the route
 * params, both tokens from the query string — which is what lets the restore
 * stay inside a `useState` initialiser. Waiting on the invite's async
 * resolution would flash the previous applicant's answers first.
 */
export interface PrequalSessionScope {
  /** The partner slug from the route, or null on the native funnel. */
  partnerSlug: string | null;
  /** The `?invite=` token on the current URL, or null. */
  inviteToken: string | null;
  /**
   * The `?blt=` continuation token handed over by the /buy landing page, or
   * null.
   *
   * OPTIONAL because a funnel that can never carry one simply omits it, and an
   * omitted member and an explicit null produce the same fingerprint (see
   * below). The seller funnel is exactly that case: `buy.html` links only to
   * `/prequalify/buyer`, and the token is minted from a BUYER lead, so a seller
   * run has no continuation token by construction rather than by convention.
   */
  buyLandingToken?: string | null;
}

/**
 * The comparable identity of a funnel context.
 *
 * JSON rather than a delimiter-joined string so no slug or token can ever
 * compose its way into somebody else's fingerprint. The slug is lowercased
 * (route case is not brand identity, and two casings of one brand are the
 * same application); the tokens are NOT — they are base64url and
 * case-carrying.
 *
 * Blank and absent collapse to the same null, so a `?blt=` with nothing after
 * it does not mint a context of its own, and a caller that omits the member
 * fingerprints identically to one that passes null for it.
 */
export function fingerprintScope(scope: PrequalSessionScope): string {
  const slug = scope.partnerSlug?.trim().toLowerCase();
  const token = scope.inviteToken?.trim();
  const buyLanding = scope.buyLandingToken?.trim();
  return JSON.stringify([
    slug ? slug : null,
    token ? token : null,
    buyLanding ? buyLanding : null,
  ]);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

interface StoredEnvelope<F> {
  state: StoredFunnelState<F>;
  /** The fingerprint the blob was written under; null when absent/garbled,
   *  which can never match a real context and so always clears. */
  scope: string | null;
}

/** Parse + shape-check one slot. Any problem → null (ignore). */
function readEnvelope<F>(key: string): StoredEnvelope<F> | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    if (!isRecord(parsed.form)) return null;
    if (typeof parsed.stepIndex !== "number" || typeof parsed.maxReached !== "number") return null;
    const finalized = isRecord(parsed.finalized)
      ? (parsed.finalized as unknown as PrequalFinalizeResult)
      : null;
    return {
      state: {
        form: parsed.form as F,
        stepIndex: parsed.stepIndex,
        maxReached: parsed.maxReached,
        finalized,
      },
      scope: typeof parsed.scope === "string" ? parsed.scope : null,
    };
  } catch {
    return null;
  }
}

/**
 * Restore a stored funnel state WITHOUT checking whose it is.
 *
 * For the read-only surfaces that display an application the funnel just
 * finished (the Result route) — see the scope note at the top of this file.
 * The funnel itself must use `loadScopedFunnelState`.
 */
export function loadFunnelState<F>(key: string): StoredFunnelState<F> | null {
  return readEnvelope<F>(key)?.state ?? null;
}

/**
 * Restore a stored funnel state only if it belongs to THIS context.
 *
 * On a mismatch the blob is removed, not skipped: it holds the previous
 * applicant's name, email and every answer, and leaving it readable by the
 * next thing that asks is the whole defect.
 */
export function loadScopedFunnelState<F>(
  key: string,
  scope: PrequalSessionScope,
): StoredFunnelState<F> | null {
  const envelope = readEnvelope<F>(key);
  if (envelope === null) return null;
  if (envelope.scope !== fingerprintScope(scope)) {
    clearFunnelState(key);
    return null;
  }
  return envelope.state;
}

export function saveFunnelState<F>(
  key: string,
  state: StoredFunnelState<F>,
  scope: PrequalSessionScope,
): void {
  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({ ...state, scope: fingerprintScope(scope) }),
    );
  } catch {
    // Quota / privacy mode — persistence is best-effort.
  }
}

export function clearFunnelState(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

// ── Deal Explorer scenario ──────────────────────────────────────────────

export interface StoredExplorerState {
  /** Grouped digit strings, exactly as the currency fields store them. */
  purchasePrice: string;
  downPayment: string;
  annualCashFlow: string;
  expectedPurchaseDate: string;
  /** The 10/20-year picker (D5). Optional: a scenario stored before the picker
   *  existed restores at the 20-year default. */
  amortizationMonths?: number;
  pinned: unknown[];
}

export function loadExplorerState(): StoredExplorerState | null {
  try {
    const raw = window.sessionStorage.getItem(PREQUAL_EXPLORER_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    if (
      typeof parsed.purchasePrice !== "string" ||
      typeof parsed.downPayment !== "string" ||
      typeof parsed.annualCashFlow !== "string" ||
      typeof parsed.expectedPurchaseDate !== "string" ||
      !Array.isArray(parsed.pinned)
    ) {
      return null;
    }
    return {
      purchasePrice: parsed.purchasePrice,
      downPayment: parsed.downPayment,
      annualCashFlow: parsed.annualCashFlow,
      expectedPurchaseDate: parsed.expectedPurchaseDate,
      // Only a recognised schedule restores; anything else falls back to the
      // 20-year default at the caller.
      amortizationMonths:
        parsed.amortizationMonths === 120 || parsed.amortizationMonths === 240
          ? parsed.amortizationMonths
          : undefined,
      pinned: parsed.pinned,
    };
  } catch {
    return null;
  }
}

export function saveExplorerState(state: StoredExplorerState): void {
  try {
    window.sessionStorage.setItem(PREQUAL_EXPLORER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // best-effort
  }
}
